import React, { MutableRefObject, ReactElement, useContext, useEffect, useRef, useState } from 'react'
import {
    Controller,
    ControllerRenderProps,
    useFormContext,
    UseFormMethods
} from '../../../../../Vendor/react-hook-form/dist/index.ie11'
import { CommonFormContext, FormContextType } from '../../../Contexts/CommonFormContext'
import { ReactState, Rules } from '../../../Types/Types'
import PopperContent from '../../PopperContent'
import TextInputDropDown from '../../TextInputDropDown'
import { SelectOptions } from '../Select/Select'
import { SelectOption } from '../Types'

/**
 * Component properties type.
 */
type Props = {
    options: SelectOptions<string | number>
    label?: string
    onChange?: (value: string | number) => void
    name: string
    rules?: Rules
    defaultValue?: string
    disabled?: boolean
}

/**
 * Select box supporting free text entry.
 *
 * @param options - Select box items.
 * @param onChange - Value change handler.
 * @param name - The name for React Hook Forms.
 * @param rules - The rules for React Hook Forms.
 * @param defaultValue - The default value for React Hook Forms.
 * @param disabled - Whether the select is disabled.
 */
export const FreeTextSelect: React.FC<Props> = ({
    options,
    label: selectLabel,
    onChange: selectOnChange,
    name,
    rules,
    defaultValue,
    disabled
}: Props): ReactElement => {
    const commonForm: Nullable<FormContextType> = useContext(CommonFormContext)

    const { control, watch, setValue, trigger }: Definable<UseFormMethods<Record<string, unknown>>> = useFormContext()

    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [textFieldValue, setTextFieldValue]: ReactState<string> = useState<string>(defaultValue || '')

    const textElemRef: MutableRefObject<null> = useRef(null)

    const currentValue: string = watch(name, '') as string

    // What happens when the user clicks on the text field.
    const handleClickOnTextField: () => void = (): void => {
        setIsOpen((wasOpen: boolean) => !wasOpen)
    }

    const handleItemClick = ({ value }: SelectOption<string | number>): void => {
        setValue(name, value)
        setIsOpen(false)
        selectOnChange && selectOnChange(value)
        trigger(name)
    }

    const toItem = (
        { value, label }: SelectOption<string | number>,
        itemClickHandler: ({ value }: SelectOption<string | number>) => void
    ): ReactElement => (
        <div
            className='fts-entry'
            key={value}
            onClick={(): void => itemClickHandler({ value, label })}
            onContextMenu={(event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
                event.preventDefault()
                itemClickHandler({ value, label })
            }}
        >
            {label}
        </div>
    )

    useEffect((): void => {
        setTextFieldValue(
            options?.find((option: SelectOption<string | number>): boolean => option.value === currentValue)?.label ??
                currentValue
        )
    }, [currentValue])

    const props = {
        control,
        name,
        rules,
        defaultValue: defaultValue ?? watch(name ?? '', ''),
        render: ({ onChange }: ControllerRenderProps<Record<string, unknown>>): ReactElement => (
            <React.Fragment>
                <div className='select-range-wrapper free-text-select'>
                    <TextInputDropDown
                        disabled={disabled}
                        value={textFieldValue}
                        labelLeft={selectLabel}
                        elementRef={textElemRef}
                        customDropdown={true}
                        isOpen={isOpen}
                        onClick={handleClickOnTextField}
                        onChange={(e): void => {
                            if (commonForm?.handleFormChange) {
                                commonForm?.handleFormChange(name)
                            }

                            onChange(e)
                        }}
                    />
                </div>
                <PopperContent
                    anchorEl={textElemRef.current}
                    open={isOpen}
                    setOpen={setIsOpen}
                    isClickAwayHandled={true}
                >
                    <div className='free-text-select'>
                        {isOpen && (
                            <div className='items-wrapper'>
                                {options.map((entry: SelectOption<string | number>) => toItem(entry, handleItemClick))}
                            </div>
                        )}
                    </div>
                </PopperContent>
            </React.Fragment>
        )
    }

    return <Controller {...props} />
}
